import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateUserCommunicationPreferences } from "@queries/profile/useUpdateUserCommunicationPreferences";
import { useUIStore } from "@store/ui.store";
import { toast } from "@shared/services/toast";
import { getPendingSyncStateQueryKey } from "@shared/generated/crm/queries/syncStateController/useGetPendingSyncState";
import { getSafeRedirectTarget } from "@shared/utils/getSafeRedirectTarget";

// Shared by both the post-OAuth-popup flow (useConnectedAppsLogic) and the
// "Grant permission" CTA for already-connected accounts (ContactsExtractionConsent).
export const useEnableGoogleContactsExtraction = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeRedirectTarget(searchParams.get("returnTo"));
  const closeSettingsModal = useUIStore((state) => state.closeSettingsModal);
  const updateUserCommunicationPreferencesMutation =
    useUpdateUserCommunicationPreferences();

  const enableGoogleContactsExtraction = useCallback(() => {
    updateUserCommunicationPreferencesMutation.mutate(
      { type: "googleEmailContactsExtraction", enabled: true },
      {
        onSuccess: () => {
          toast.success(
            "Google connected! We're now importing your contacts.",
            {
              toastId: "google-connected-success",
              position: "bottom-right",
            },
          );
          queryClient.invalidateQueries({
            queryKey: getPendingSyncStateQueryKey(),
          });
          if (returnTo) {
            window.location.replace(returnTo);
          } else {
            closeSettingsModal();
          }
        },
      },
    );
  }, [
    updateUserCommunicationPreferencesMutation,
    queryClient,
    returnTo,
    closeSettingsModal,
  ]);

  return {
    enableGoogleContactsExtraction,
    isEnabling: updateUserCommunicationPreferencesMutation.isPending,
  };
};
